import { useCallback, useRef, useState } from "react";
import { AmountField } from "../components/AmountField";
import { AsyncPanel } from "../components/AsyncPanel";
import { Button } from "../components/Button";
import { Callout } from "../components/Callout";
import { CheckboxField, SelectField, TextAreaField, TextField } from "../components/Field";
import { Skeleton } from "../components/Skeleton";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { ApiError } from "../api/client";
import { editableValuesOf } from "../api/deal-edit";
import { useApiResource } from "../hooks/useApiResource";
import { useSession } from "../session/useSession";
import { messageForDealEditError } from "../lib/deal-edit-messages";
import { DEAL_TYPE_OPTIONS, buildDealPatch, formValuesOf } from "../lib/deal-form";
import { joinFieldLabels, labelForFieldName } from "../lib/deal-fields";
import styles from "./DealEditor.module.css";
import type { FormEvent } from "react";
import type { DealFormValues, FieldErrors } from "../lib/deal-form";
import type {
  DealEditPatch,
  DealEditPolicy,
  EditableDealField,
  SavedDeal,
  SellerDeal,
  SellerDealDetail,
} from "../api/types";

/**
 * Editing one of the founder's own deals (canon APP-005, CORE-015, decision D-005).
 *
 * The editor lives **inside the deal's own card**, not on a page or in a dialog of its own.
 * The reason a founder is usually here is that Dealbid asked for changes, and the sentence
 * saying so is on that card: taking them somewhere else to make the change would separate
 * the request from the response to it.
 *
 * Three things are load-bearing.
 *
 * **The rule shown is core's rule.** Which fields may be edited, which of them are canon
 * § 3 verification facts, and whether a material edit sends *this* deal back to review, all
 * come from the `editPolicy` core publishes with the deal. Nothing here classifies a field.
 * A UI holding its own copy would drift the first time a canon decision changed the list,
 * and would then warn a founder about the wrong consequence — which is worse than not
 * warning them, because it would be believed.
 *
 * **The consequence is never softened.** When a pending change is material and the deal is
 * approved, the founder is told before saving, in plain words, that the deal leaves the
 * public board and cannot bid until it is approved again. That sentence is not tucked
 * behind a disclosure arrow and does not appear only after the fact.
 *
 * **Only what changed is sent.** The patch is the difference against the deal core sent, so
 * saving after touching one box does not ask core to re-evaluate eleven fields, and
 * `changedFields` in the answer means what it says.
 *
 * The deal is re-read when the editor opens rather than being edited from the list's copy.
 * `returnsToReview` depends on the deal's status, and an operator can approve or take down
 * a deal while the desk sits open; editing from a stale copy would show a warning that was
 * true when the page loaded.
 */

export interface DealEditorProps {
  /** The deal as the desk listed it. Its id is core's, and addresses the edit. */
  deal: SellerDeal;
  loadDetail: (dealId: string) => Promise<SellerDealDetail>;
  saveDeal: (input: { dealId: string; patch: DealEditPatch }) => Promise<SavedDeal>;
  /** Called with core's own answer once an edit is accepted. */
  onSaved: (saved: SavedDeal) => void;
  /** Leaves the editor without saving. */
  onClose: () => void;
}

export function DealEditor({ deal, loadDetail, saveDeal, onSaved, onClose }: DealEditorProps) {
  const { retry } = useSession();
  const headingId = `deal-editor-${deal.id}`;
  const heading = useRef<HTMLHeadingElement>(null);

  const loader = useCallback(() => loadDetail(deal.id), [loadDetail, deal.id]);
  const detail = useApiResource(loader);

  // Focus moves to the editor's own heading when it opens, so a keyboard or screen-reader
  // user is taken to what just appeared rather than left at the button that opened it. The
  // ref callback fires when the heading mounts, which is after the read lands.
  const focusHeading = useCallback((node: HTMLHeadingElement | null) => {
    heading.current = node;
    node?.focus();
  }, []);

  return (
    <section className={styles.editor} aria-labelledby={headingId}>
      <h4 className={styles.heading} id={headingId} tabIndex={-1} ref={focusHeading}>
        Edit {deal.productName}
      </h4>

      <AsyncPanel
        state={detail.state}
        reload={detail.reload}
        onSessionEnded={retry}
        loadingLabel={`Loading ${deal.productName} for editing`}
        skeleton={<EditorSkeleton />}
        errorTitle="This deal didn't open for editing"
      >
        {(data) => (
          <EditForm
            detail={data}
            saveDeal={saveDeal}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </AsyncPanel>
    </section>
  );
}

function EditorSkeleton() {
  return (
    <div className={styles.skeleton}>
      <Skeleton width="100%" height="2.75rem" />
      <Skeleton width="100%" height="2.75rem" />
      <Skeleton width="70%" height="2.75rem" />
    </div>
  );
}

/**
 * Why a deal cannot be edited, worded from its status rather than from the refusal code.
 *
 * `editPolicy.editable` is the decision and it is core's; this only chooses the sentence.
 * Canon § 15 separates a change request from a takedown, so the two cases do not share
 * wording: one deal was refused, the other was removed, and in both cases reversing the
 * decision is the operator's call rather than the founder's.
 */
function notEditableCopy(deal: SellerDeal): { title: string; body: string } {
  if (deal.status === "rejected") {
    return {
      title: "This deal can't be edited",
      body: "Dealbid did not approve it, and editing it here would not put it back in the queue. Contact Dealbid if you want to discuss the decision or submit a different deal.",
    };
  }
  if (deal.status === "disabled") {
    return {
      title: "This deal can't be edited",
      body: "Dealbid has taken it off the board. Reversing that is Dealbid's decision rather than an edit, so contact Dealbid to discuss it.",
    };
  }
  // Core decided this deal is not editable for a reason this app does not have a sentence
  // for. Saying so plainly beats inventing an explanation the response does not support.
  return {
    title: "This deal can't be edited right now",
    body: "Dealbid is not accepting changes to it at the moment. Contact Dealbid if you need it changed.",
  };
}

type SaveStatus = "idle" | "submitting" | "error";

function EditForm({
  detail,
  saveDeal,
  onSaved,
  onClose,
}: {
  detail: SellerDealDetail;
  saveDeal: DealEditorProps["saveDeal"];
  onSaved: DealEditorProps["onSaved"];
  onClose: DealEditorProps["onClose"];
}) {
  const { deal, editPolicy } = detail;
  const current = editableValuesOf(deal);

  const [form, setForm] = useState<DealFormValues>(() => formValuesOf(current));
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [otherIssues, setOtherIssues] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [nothingChanged, setNothingChanged] = useState(false);
  const inFlight = useRef(false);

  const set = useCallback(<K extends keyof DealFormValues>(key: K, value: DealFormValues[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    // Editing is the founder starting again: last time's verdict no longer describes what
    // is in the boxes, and leaving it on screen would make a stale error look current.
    setStatus("idle");
    setFieldErrors({});
    setOtherIssues([]);
    setFailure(null);
    setNothingChanged(false);
  }, []);

  if (!editPolicy.editable) {
    const copy = notEditableCopy(deal);
    return (
      <div className={styles.refused}>
        <Callout tone="warning" title={copy.title}>
          {copy.body}
        </Callout>
        <Button variant="secondary" onClick={onClose}>
          Close
          <VisuallyHidden> the editor for {deal.productName}</VisuallyHidden>
        </Button>
      </div>
    );
  }

  const offered = new Set<EditableDealField>(editPolicy.editableFields);
  const material = new Set<EditableDealField>(editPolicy.materialFields);
  const offers = (field: EditableDealField) => offered.has(field);

  const diff = buildDealPatch(current, form);
  const pendingChanges = diff.ok ? diff.changedFields : [];
  const pendingMaterial = pendingChanges.filter((field) => material.has(field));

  /** For a field core does not treat as a verification fact, said once, where it applies. */
  const safeNote = (field: EditableDealField) =>
    material.has(field) || !editPolicy.returnsToReview
      ? undefined
      : "Changing this does not send the deal back to review.";

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (inFlight.current) return;

    if (!diff.ok) {
      setStatus("error");
      setFieldErrors(diff.fieldErrors);
      setFailure(null);
      return;
    }

    if (diff.changedFields.length === 0) {
      // Core refuses an empty patch rather than answering `200`, because "nothing was sent"
      // is indistinguishable from a UI that dropped the founder's changes. Saying so here
      // spends no request to be told the same thing.
      setNothingChanged(true);
      return;
    }

    inFlight.current = true;
    setStatus("submitting");
    setFieldErrors({});
    setOtherIssues([]);
    setFailure(null);

    void saveDeal({ dealId: deal.id, patch: diff.patch })
      .then((saved) => {
        onSaved(saved);
      })
      .catch((caught: unknown) => {
        const error =
          caught instanceof ApiError
            ? caught
            : new ApiError("Dealbid couldn't save those changes.", { status: null });

        // Core names the offending inputs, so each message is shown against the box it is
        // about. A field this app draws no control for still has its message read out at
        // the foot of the form rather than being dropped.
        const named: FieldErrors = {};
        const unnamed: string[] = [];
        for (const issue of error.fields) {
          if (issue.field !== "" && offered.has(issue.field as EditableDealField)) {
            named[issue.field as EditableDealField] = issue.message;
          } else {
            unnamed.push(
              issue.field === ""
                ? issue.message
                : `${labelForFieldName(issue.field)}: ${issue.message}`,
            );
          }
        }

        setFieldErrors(named);
        setOtherIssues(unnamed);
        setFailure(messageForDealEditError(error));
        setStatus("error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }

  const busy = status === "submitting";

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <p className={styles.intro}>
        Change what you told Dealbid about this deal. Everything Dealbid decided — its review
        state, its badges and whether it is verified — stays with Dealbid.
      </p>

      <div className={styles.fields}>
        {offers("productName") ? (
          <TextField
            label="Product name"
            context={deal.productName}
            value={form.productName}
            onChange={(value) => {
              set("productName", value);
            }}
            disabled={busy}
            {...(fieldErrors.productName === undefined ? {} : { error: fieldErrors.productName })}
            {...(safeNote("productName") === undefined ? {} : { hint: safeNote("productName") })}
          />
        ) : null}

        {offers("productUrl") ? (
          <TextField
            label="Product website"
            type="url"
            value={form.productUrl}
            onChange={(value) => {
              set("productUrl", value);
            }}
            disabled={busy}
            hint={safeNote("productUrl") ?? "The site Dealbid checked the product on."}
            {...(fieldErrors.productUrl === undefined ? {} : { error: fieldErrors.productUrl })}
          />
        ) : null}

        {offers("shortDescription") ? (
          <TextAreaField
            label="Short description"
            value={form.shortDescription}
            onChange={(value) => {
              set("shortDescription", value);
            }}
            disabled={busy}
            hint={safeNote("shortDescription") ?? "What a buyer reads on the board."}
            {...(fieldErrors.shortDescription === undefined
              ? {}
              : { error: fieldErrors.shortDescription })}
          />
        ) : null}

        {offers("dealUrl") ? (
          <TextField
            label="Deal link"
            type="url"
            value={form.dealUrl}
            onChange={(value) => {
              set("dealUrl", value);
            }}
            disabled={busy}
            hint={safeNote("dealUrl") ?? "Where a buyer lands when they open the deal."}
            {...(fieldErrors.dealUrl === undefined ? {} : { error: fieldErrors.dealUrl })}
          />
        ) : null}

        {offers("dealType") ? (
          <SelectField
            label="Deal type"
            value={form.dealType}
            onChange={(value) => {
              set("dealType", value);
            }}
            options={DEAL_TYPE_OPTIONS}
            placeholder="Choose a deal type"
            disabled={busy}
            {...(fieldErrors.dealType === undefined ? {} : { error: fieldErrors.dealType })}
            {...(safeNote("dealType") === undefined ? {} : { hint: safeNote("dealType") })}
          />
        ) : null}

        <div className={styles.pair}>
          {offers("originalPriceCents") ? (
            <AmountField
              label="Usual price"
              value={form.originalPrice}
              onChange={(value) => {
                set("originalPrice", value);
              }}
              disabled={busy}
              hint="Leave empty if this deal has no before price."
              {...(fieldErrors.originalPriceCents === undefined
                ? {}
                : { error: fieldErrors.originalPriceCents })}
            />
          ) : null}
          {offers("dealPriceCents") ? (
            <AmountField
              label="Deal price"
              value={form.dealPrice}
              onChange={(value) => {
                set("dealPrice", value);
              }}
              disabled={busy}
              hint="What a buyer pays through this deal."
              {...(fieldErrors.dealPriceCents === undefined
                ? {}
                : { error: fieldErrors.dealPriceCents })}
            />
          ) : null}
        </div>

        {offers("valueNotes") ? (
          <TextAreaField
            label="What the deal is worth"
            optional
            value={form.valueNotes}
            onChange={(value) => {
              set("valueNotes", value);
            }}
            disabled={busy}
            hint={
              safeNote("valueNotes") ??
              "Required instead of prices when the value isn't a before and after figure."
            }
            {...(fieldErrors.valueNotes === undefined ? {} : { error: fieldErrors.valueNotes })}
          />
        ) : null}

        {offers("couponCode") ? (
          <TextField
            label="Coupon code"
            optional
            value={form.couponCode}
            onChange={(value) => {
              set("couponCode", value);
            }}
            disabled={busy}
            hint={safeNote("couponCode") ?? "How the buyer actually gets the price. Empty clears it."}
            {...(fieldErrors.couponCode === undefined ? {} : { error: fieldErrors.couponCode })}
          />
        ) : null}

        {offers("limitations") ? (
          <TextAreaField
            label="Limitations"
            optional
            value={form.limitations}
            onChange={(value) => {
              set("limitations", value);
            }}
            disabled={busy}
            hint={safeNote("limitations") ?? "What a buyer should know before they commit."}
            {...(fieldErrors.limitations === undefined ? {} : { error: fieldErrors.limitations })}
          />
        ) : null}

        {offers("isExclusive") ? (
          <CheckboxField
            label="This offer is exclusive to Dealbid"
            checked={form.isExclusive}
            onChange={(checked) => {
              set("isExclusive", checked);
            }}
            disabled={busy}
            hint={
              safeNote("isExclusive") ??
              "Only tick this while the offer and its terms are not available elsewhere."
            }
            {...(fieldErrors.isExclusive === undefined ? {} : { error: fieldErrors.isExclusive })}
          />
        ) : null}

        {offers("logoUrl") ? (
          <TextField
            label="Logo image URL"
            optional
            type="url"
            value={form.logoUrl}
            onChange={(value) => {
              set("logoUrl", value);
            }}
            disabled={busy}
            hint={safeNote("logoUrl") ?? "Shown beside the deal. Empty clears it."}
            {...(fieldErrors.logoUrl === undefined ? {} : { error: fieldErrors.logoUrl })}
          />
        ) : null}
      </div>

      <ReviewConsequence
        editPolicy={editPolicy}
        pendingMaterial={pendingMaterial}
        pendingChanges={pendingChanges}
      />

      <div className={styles.actions}>
        {/*
          * The visible words are hidden from assistive technology and the whole name is
          * given once beside them. A visible label followed by a hidden fragment reads as
          * one run of text with no space between the two — "Save changesto Acme Analytics" —
          * because an accessible name concatenates adjacent inline nodes after trimming
          * each. Saying the name once, in one node, is the only way to be sure of it.
          */}
        <Button type="submit" disabled={busy}>
          <span aria-hidden="true">{busy ? "Saving…" : "Save changes"}</span>
          <VisuallyHidden>
            {busy ? "Saving changes" : "Save changes"} to {deal.productName}
          </VisuallyHidden>
        </Button>
        <Button variant="quiet" onClick={onClose} disabled={busy}>
          <span aria-hidden="true">Cancel</span>
          <VisuallyHidden>Cancel editing {deal.productName}</VisuallyHidden>
        </Button>
      </div>

      {nothingChanged ? (
        <p className={styles.note} role="status">
          Nothing has changed yet, so there is nothing to save.
        </p>
      ) : null}

      {failure === null ? null : (
        <div className={styles.failure} role="alert">
          <p>
            <VisuallyHidden>Error: </VisuallyHidden>
            {failure}
          </p>
          {otherIssues.length === 0 ? null : (
            <ul className={styles.issues}>
              {otherIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </form>
  );
}

/**
 * What saving will do, said before it is done.
 *
 * The three states are different facts, not three tones of the same one:
 *
 *   - a pending **material** change to an **approved** deal will invalidate the approval,
 *     and the consequence — off the public board, unable to bid, until Dealbid approves it
 *     again — is stated in full. Canon § 3 makes these fields what review actually checked,
 *     and `AGENTS.md` § 14 makes it a ranking-integrity rule rather than only a moderation
 *     one, so this sentence is never softened into "may need re-review";
 *   - a deal **already in review** keeps its status through any edit, so a founder fixing
 *     what was asked of them is not warned about a consequence that will not happen;
 *   - an approved deal with nothing material pending stays on the board, and saying so is
 *     what makes the warning above meaningful when it does appear.
 *
 * `editPolicy.returnsToReview` is core's answer for *this* deal right now, never a status
 * comparison made here.
 */
function ReviewConsequence({
  editPolicy,
  pendingMaterial,
  pendingChanges,
}: {
  editPolicy: DealEditPolicy;
  pendingMaterial: readonly EditableDealField[];
  pendingChanges: readonly EditableDealField[];
}) {
  if (!editPolicy.returnsToReview) {
    return (
      <p className={styles.steady} role="status">
        This deal is already in review, and saving these changes keeps it there. Dealbid
        reviews it as it now stands.
      </p>
    );
  }

  if (pendingMaterial.length === 0) {
    return (
      <p className={styles.steady} role="status">
        {pendingChanges.length === 0
          ? "Nothing is changed yet. This deal stays on the public board while it is approved."
          : "These changes keep the deal on the public board — none of them is something Dealbid verified."}
      </p>
    );
  }

  return (
    <div className={styles.consequence} role="status">
      <p className={styles.consequenceTitle}>
        Saving sends this deal back to review
      </p>
      <p>
        You have changed {joinFieldLabels(pendingMaterial)}, which is what Dealbid checked
        when it approved this deal.
      </p>
      <p>
        When you save, the deal leaves the public board and cannot bid for a Top 10 position
        until Dealbid approves it again. A standing bid keeps its reserved credit until
        tomorrow&rsquo;s board is settled.
      </p>
    </div>
  );
}
